import React from 'react';
import { RogItemStatus } from '../../../types';
import {
  coerceStatusList,
  groupPositions,
  parsePositionKey,
  RogItemStatusValue,
  RogPositionResultCamel,
  ROG_STATUS_COLOR,
  ROG_STATUS_LABEL,
  ROG_STATUS_VALUES,
} from './rogStatusUtils';

interface GtRogModGridProps {
  rogResultsByPosition: Record<string, RogPositionResultCamel>;
  selectedPositionKey: string | null;
  onCellClick: (positionKey: string) => void;
  onCellRightClick: (positionKey: string, event: React.MouseEvent) => void;
  onRemoveRogPosition?: (mod: number, shelf: number, column: number) => void;
  onAddRogPosition?: (
    mod: number,
    shelf: number,
    column: number,
    status: RogItemStatusValue
  ) => void;
  visibleModNumbers?: Set<number>;
  pickableModNumbers?: Set<number>;
  modNumberToTagName?: Map<number, string>;
}

export const GtRogModGrid: React.FC<GtRogModGridProps> = ({
  rogResultsByPosition,
  selectedPositionKey,
  onCellClick,
  onCellRightClick,
  onRemoveRogPosition,
  onAddRogPosition,
  visibleModNumbers,
  pickableModNumbers,
  modNumberToTagName,
}) => {
  const modLabel = React.useCallback(
    (mod: number) => modNumberToTagName?.get(mod) ?? `Mod ${mod}`,
    [modNumberToTagName]
  );
  const grouped = React.useMemo(() => groupPositions(rogResultsByPosition), [rogResultsByPosition]);

  const mods = React.useMemo(() => {
    const all = [...grouped.keys()].sort((a, b) => a - b);
    if (!visibleModNumbers) return all;
    return all.filter((m) => visibleModNumbers.has(m));
  }, [grouped, visibleModNumbers]);

  const selectableMods = React.useMemo(() => {
    if (pickableModNumbers) return [...pickableModNumbers].sort((a, b) => a - b);
    return mods;
  }, [pickableModNumbers, mods]);

  const [editMod, setEditMod] = React.useState<number | null>(null);
  const [editShelf, setEditShelf] = React.useState<string>('');
  const [editColumn, setEditColumn] = React.useState<string>('');
  const [editStatus, setEditStatus] = React.useState<RogItemStatusValue>(RogItemStatus.CORRECT);

  React.useEffect(() => {
    if (editMod === null && selectableMods.length > 0) {
      setEditMod(selectableMods[0]);
    } else if (editMod !== null && !selectableMods.includes(editMod) && selectableMods.length > 0) {
      setEditMod(selectableMods[0]);
    }
  }, [selectableMods, editMod]);

  React.useEffect(() => {
    if (!selectedPositionKey) return;
    const parsed = parsePositionKey(selectedPositionKey);
    if (!parsed) return;
    setEditMod(parsed.mod);
    setEditShelf(String(parsed.shelf));
    setEditColumn(String(parsed.column));
  }, [selectedPositionKey]);

  const showControls = !!(onRemoveRogPosition || onAddRogPosition);

  const parseControlInputs = (): { mod: number; shelf: number; column: number } | null => {
    if (editMod === null) return null;
    const shelf = Number(editShelf);
    const column = Number(editColumn);
    if (!Number.isInteger(shelf) || shelf <= 0) return null;
    if (!Number.isInteger(column) || column <= 0) return null;
    return { mod: editMod, shelf, column };
  };

  if (grouped.size === 0 && !showControls) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 text-sm text-gray-500">
        No ROG positions found in this sample.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      {showControls && (
        <div className="flex flex-wrap items-center gap-2 mb-3 p-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-xs text-gray-600">Edit:</span>
          <select
            value={editMod ?? ''}
            onChange={(e) => setEditMod(e.target.value === '' ? null : Number(e.target.value))}
            className="text-xs px-2 py-1 border border-gray-300 rounded"
          >
            {selectableMods.length === 0 && <option value="">No mods</option>}
            {selectableMods.map((mod) => (
              <option key={mod} value={mod}>
                {modLabel(mod)}
              </option>
            ))}
          </select>
          <input
            type="number"
            min={1}
            value={editShelf}
            onChange={(e) => setEditShelf(e.target.value)}
            placeholder="Shelf"
            className="w-20 text-xs px-2 py-1 border border-gray-300 rounded"
          />
          <input
            type="number"
            min={1}
            value={editColumn}
            onChange={(e) => setEditColumn(e.target.value)}
            placeholder="Position"
            className="w-20 text-xs px-2 py-1 border border-gray-300 rounded"
          />
          {onAddRogPosition && (
            <select
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as RogItemStatusValue)}
              className="text-xs px-2 py-1 border border-gray-300 rounded"
            >
              {ROG_STATUS_VALUES.map((status) => (
                <option key={status} value={status}>
                  {ROG_STATUS_LABEL[status]}
                </option>
              ))}
            </select>
          )}
          {onAddRogPosition && (
            <button
              type="button"
              onClick={() => {
                const parsed = parseControlInputs();
                if (!parsed) return;
                onAddRogPosition(parsed.mod, parsed.shelf, parsed.column, editStatus);
              }}
              className="text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              Add
            </button>
          )}
          {onRemoveRogPosition && (
            <button
              type="button"
              onClick={() => {
                const parsed = parseControlInputs();
                if (!parsed) return;
                onRemoveRogPosition(parsed.mod, parsed.shelf, parsed.column);
              }}
              className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
            >
              Remove
            </button>
          )}
        </div>
      )}
      {grouped.size === 0 ? (
        <div className="text-sm text-gray-500 mb-2">No ROG positions yet — add one above.</div>
      ) : mods.length === 0 ? (
        <div className="text-sm text-gray-500 mb-2">No mods match the current filter.</div>
      ) : null}
      <div className="flex gap-4">
        {mods.map((mod) => {
          const byShelf = grouped.get(mod)!;
          const shelves = [...byShelf.keys()].sort((a, b) => a - b);

          return (
            <div
              key={mod}
              className="flex flex-col border border-gray-200 rounded-md overflow-hidden"
              style={{ flex: '1 1 0', minWidth: 0 }}
            >
              <div className="px-2 py-1 bg-gray-100 text-xs font-mono text-gray-600 border-b border-gray-200">
                {modLabel(mod)}
              </div>
              <div className="flex flex-col" style={{ minHeight: 200 }}>
                {shelves.map((shelf) => {
                  const cells = byShelf.get(shelf)!;
                  return (
                    <div
                      key={shelf}
                      className="flex border-b border-gray-100 last:border-b-0"
                      style={{ flex: '1 1 0', minHeight: 36 }}
                    >
                      {cells.map(({ column, key, value }) => {
                        const statuses = coerceStatusList(value.rogItemStatus);
                        const isSelected = selectedPositionKey === key;
                        const hasStatuses = statuses.length > 0;
                        const isUnassigned =
                          !value.listOfPrUuids || value.listOfPrUuids.length === 0;

                        const outline = isSelected
                          ? '2px solid #2563eb'
                          : isUnassigned
                            ? '2px solid #dc2626'
                            : 'none';

                        const titleParts = [
                          `${shelf}-${column}`,
                          hasStatuses ? statuses.join(', ') : '(no status)',
                        ];
                        if (isUnassigned) titleParts.push('no PR detections linked');

                        return (
                          <div
                            key={key}
                            onClick={() => onCellClick(key)}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              onCellRightClick(key, e);
                            }}
                            className="relative flex items-center justify-center cursor-pointer border-r border-gray-100 last:border-r-0 hover:ring-2 hover:ring-blue-300 hover:z-10"
                            style={{
                              flex: '1 1 0',
                              outline,
                              outlineOffset: '-2px',
                              zIndex: isSelected || isUnassigned ? 5 : undefined,
                            }}
                            title={titleParts.join(' · ')}
                          >
                            {hasStatuses ? (
                              <div className="absolute inset-0 flex">
                                {statuses.map((status, i) => (
                                  <div
                                    key={`${status}-${i}`}
                                    style={{
                                      flex: '1 1 0',
                                      backgroundColor: ROG_STATUS_COLOR[status],
                                      opacity: 0.6,
                                    }}
                                  />
                                ))}
                              </div>
                            ) : (
                              <div
                                className="absolute inset-0"
                                style={{
                                  background:
                                    'repeating-linear-gradient(45deg, #f3f4f6, #f3f4f6 4px, #e5e7eb 4px, #e5e7eb 8px)',
                                }}
                              />
                            )}
                            <span className="relative text-xs font-medium text-gray-900 mix-blend-luminosity">
                              {shelf}-{column}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
